/**
 * Locale Configuration
 * Centralized country-specific configurations for currency, pricing, banks, and defaults
 *
 * TIER DISCOUNT STRUCTURE (Applied to basePerCreditRate):
 * - Tier 1 (10-25 credits): 20% discount (Standard)
 * - Tier 2 (26-100 credits): 30% discount (Bulk)
 * - Tier 3 (101-499 credits): 40% discount (Premium)
 * - Tier 4 (500+ credits): 50% discount (Super Premium)
 */

export const TIER_DISCOUNTS = [
  { min: 10, max: 25, discount: 20, name: 'Standard' },
  { min: 26, max: 100, discount: 30, name: 'Bulk' },
  { min: 101, max: 499, discount: 40, name: 'Premium' },
  { min: 500, max: Infinity, discount: 50, name: 'Super Premium' }
];

export const LOCALE_CONFIG = {
  US: {
    code: 'US',
    name: 'United States',
    flag: '🇺🇸',
    language: 'en',
    currency: {
      symbol: '$',
      code: 'USD'
    },
    pricePerPage: 0.20,
    basePerCreditRate: 0.20,  // Base rate before tier discounts
    bulkPricing: {
      25: 4,
      100: 14,
      500: 50
    },
    banks: 'Bank of America, Chase, Wells Fargo, Citibank, US Bank',
    defaultDateFormat: 'MM/DD/YYYY'
  },

  CA: {
    code: 'CA',
    name: 'Canada',
    flag: '🇨🇦',
    language: 'en',
    currency: {
      symbol: 'C$',
      code: 'CAD'
    },
    pricePerPage: 0.20,
    basePerCreditRate: 0.20,  // Base rate before tier discounts
    bulkPricing: {
      25: 4,
      100: 14,
      500: 50
    },
    banks: 'TD, RBC, Scotiabank, BMO, CIBC',
    defaultDateFormat: 'DD/MM/YYYY'
  },

  GB: {
    code: 'GB',
    name: 'United Kingdom',
    flag: '🇬🇧',
    language: 'en',
    currency: {
      symbol: '£',
      code: 'GBP'
    },
    pricePerPage: 0.20,
    basePerCreditRate: 0.20,  // Base rate before tier discounts
    bulkPricing: {
      25: 4,
      100: 14,
      500: 50
    },
    banks: 'Barclays, HSBC, Lloyds, NatWest, Santander UK',
    defaultDateFormat: 'DD/MM/YYYY'
  },

  BR: {
    code: 'BR',
    name: 'Brazil',
    flag: '🇧🇷',
    language: 'pt',
    currency: {
      symbol: 'R$',
      code: 'BRL'
    },
    pricePerPage: 1,
    basePerCreditRate: 0.80,  // Base rate before tier discounts
    bulkPricing: {
      25: 20,
      100: 70,
      500: 250
    },
    banks: 'Banco do Brasil, Itaú, Bradesco, Santander, Caixa',
    defaultDateFormat: 'DD/MM/YYYY'
  },

  AR: {
    code: 'AR',
    name: 'Argentina',
    flag: '🇦🇷',
    language: 'es',
    currency: {
      symbol: '$',
      code: 'ARS'
    },
    pricePerPage: 150,
    basePerCreditRate: 120,  // Base rate before tier discounts
    bulkPricing: {
      25: 3000,
      100: 10500,
      500: 37500
    },
    banks: 'Banco Nación, Santander, BBVA, Galicia, Macro',
    defaultDateFormat: 'DD/MM/YYYY'
  },

  AE: {
    code: 'AE',
    name: 'United Arab Emirates',
    flag: '🇦🇪',
    language: 'en',
    currency: {
      symbol: 'د.إ',
      code: 'AED'
    },
    pricePerPage: 1,
    basePerCreditRate: 0.80,  // Base rate before tier discounts
    bulkPricing: {
      25: 20,
      100: 70,
      500: 250
    },
    banks: 'Emirates NBD, Abu Dhabi Commercial Bank, Dubai Islamic Bank, RAKBank',
    defaultDateFormat: 'DD/MM/YYYY'
  },

  ZA: {
    code: 'ZA',
    name: 'South Africa',
    flag: '🇿🇦',
    language: 'en',
    currency: {
      symbol: 'R',
      code: 'ZAR'
    },
    pricePerPage: 2,
    basePerCreditRate: 2.00,  // Base rate before tier discounts
    bulkPricing: {
      25: 40,
      100: 140,
      500: 500
    },
    banks: 'FNB, Standard Bank, ABSA, Nedbank, Capitec',
    defaultDateFormat: 'DD/MM/YYYY'
  },

  DEFAULT: {
    code: 'DEFAULT',
    name: 'Worldwide',
    flag: '🌍',
    language: 'en',
    currency: {
      symbol: '$',
      code: 'USD'
    },
    pricePerPage: 0.20,
    basePerCreditRate: 0.20,  // Base rate before tier discounts
    bulkPricing: {
      25: 4,
      100: 14,
      500: 50
    },
    banks: 'International banks',
    defaultDateFormat: 'DD/MM/YYYY'
  }
};

/**
 * Get locale configuration for a country code
 * @param {string} countryCode - ISO country code (e.g., 'US', 'BR', 'ZA')
 * @returns {Object} Locale configuration object
 */
export function getLocaleConfig(countryCode) {
  const config = LOCALE_CONFIG[countryCode];

  if (!config) {
    console.warn(`⚠️ No locale config for country: ${countryCode}, using DEFAULT`);
    return LOCALE_CONFIG.DEFAULT;
  }

  return config;
}

/**
 * Format price with currency symbol
 * @param {number} amount - Price amount
 * @param {Object} locale - Locale configuration object
 * @param {boolean} showCode - Whether to show currency code (e.g., USD)
 * @returns {string} Formatted price (e.g., "$4.00" or "$4.00 USD")
 */
export function formatPrice(amount, locale, showCode = false) {
  const { currency } = locale;

  // Handle Brazilian Real formatting (R$ symbol before number)
  if (currency.code === 'BRL') {
    const formatted = `${currency.symbol}${amount.toFixed(2)}`;
    return showCode ? `${formatted} ${currency.code}` : formatted;
  }

  // Handle Argentine Peso formatting ($ symbol, no decimal for large amounts)
  if (currency.code === 'ARS') {
    const formatted = `${currency.symbol}${amount.toLocaleString('es-AR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
    return showCode ? `${formatted} ${currency.code}` : formatted;
  }

  // Handle AED (د.إ after number)
  if (currency.code === 'AED') {
    const formatted = `${amount.toFixed(2)} ${currency.symbol}`;
    return showCode ? `${formatted} ${currency.code}` : formatted;
  }

  // Standard formatting (USD, CAD, GBP, ZAR)
  const formatted = `${currency.symbol}${amount.toFixed(2)}`;
  return showCode ? `${formatted} ${currency.code}` : formatted;
}

/**
 * Get list of all supported country codes
 * @returns {string[]} Array of country codes
 */
export function getSupportedCountries() {
  return Object.keys(LOCALE_CONFIG).filter(code => code !== 'DEFAULT');
}

/**
 * Check if a country has non-English language
 * @param {string} countryCode - ISO country code
 * @returns {boolean} True if language is not English
 */
export function hasNonEnglishLanguage(countryCode) {
  const config = getLocaleConfig(countryCode);
  return config.language !== 'en';
}

/**
 * Calculate tier pricing for a given quantity and locale
 * @param {number} quantity - Number of credits
 * @param {Object} locale - Locale configuration object
 * @returns {Object} Pricing details including tier info, per-credit rate, and total
 */
export function calculateTierPricing(quantity, locale) {
  // Find the appropriate tier
  const tier = TIER_DISCOUNTS.find(t => quantity >= t.min && quantity <= t.max);

  if (!tier) {
    throw new Error(`Invalid quantity: ${quantity}. Must be between 10 and 1000.`);
  }

  // Calculate per-credit rate with discount applied
  const discountMultiplier = (100 - tier.discount) / 100;
  const pricePerCredit = locale.basePerCreditRate * discountMultiplier;
  const totalPrice = quantity * pricePerCredit;

  // Calculate savings
  const standardPrice = quantity * locale.basePerCreditRate;
  const savings = standardPrice - totalPrice;

  return {
    quantity,
    pricePerCredit,
    totalPrice,
    savings: savings > 0 ? savings : 0,
    savingsPercentage: tier.discount,
    tier: tier.name,
    tierInfo: tier
  };
}
